// /* */
import { checkHasMoreItem, isDefine } from 'helpers';
import { showError } from 'helpers/handleError';
import { useEffect, useState, useCallback } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { flatten, isObject } from 'lodash';
import DomainServices from 'services/DomainServices';

/**
 *  limit: 20,
    offset: 0,
    name: '',
    active: true,
    hasActiveDomain: true,
*/
const useGetAllDomainGroup = (filters, { isTrigger = true } = { isTrigger: true }) => {
  //! State
  const [data, setData] = useState({
    domainGroups: [],
    total: 0,
    totalDomain: 0,
  });
  const [loading, setLoading] = useState(false);
  const [refetching, setRefetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  //! Function
  const fetch = useCallback(() => {
    if (!isTrigger) {
      return;
    }

    return new Promise(async (resolve, reject) => {
      try {
        const response = await DomainServices.getAllDomainGroup(filters);
        resolve(response);
      } catch (error) {
        showError(error);
        setError(error);
        reject(error);
      }
    });
  }, [filters, isTrigger]);

  const checkConditionPass = useCallback(
    (response, { isLoadmore } = {}) => {
      if (response?.data) {
        //* Check condition of response here to set data
        if (isLoadmore) {
          setData((prev) => {
            const temp = cloneDeep(prev);
            return {
              domainGroups:
                [...(temp?.domainGroups || []), ...(response?.data?.domainGroups || [])] || [],
              total: response?.data?.total || 0,
              totalDomain: response?.data?.totalDomain,
            };
          });
        } else {
          setData({
            domainGroups: response?.data?.domainGroups || [],
            total: response?.data?.total,
            totalDomain: response?.data?.totalDomain,
          });
        }

        setHasMore(checkHasMoreItem(response?.data?.domainGroups, filters.limit));
      }
    },
    [filters.limit]
  );

  const fetchMore = useCallback(
    async (shouldSetData) => {
      const isLoadmore = true;
      setLoadingMore(true);
      const response = await fetch({ isLoadmore });
      if (shouldSetData) {
        checkConditionPass(response, { isLoadmore });
      }
      setLoadingMore(false);
    },
    [fetch, checkConditionPass]
  );

  //* Refetch implicity (without changing loading state)
  const refetch = useCallback(async () => {
    setRefetching(true);
    const offset = filters.offset || 0;
    const limit = filters.limit || 0;

    const totalPage = Math.ceil(offset / limit) + 1;

    let listRequest = [];
    for (let eachPage = 0; eachPage < totalPage; eachPage++) {
      const request = DomainServices.getAllDomainGroup({
        ...filters,
        offset: eachPage * filters.limit,
      });
      listRequest = [...listRequest, request];
    }

    const responses = await Promise.allSettled(listRequest);
    const allData = responses.map((el) => (isObject(el?.value?.data) ? el?.value?.data : {}));
    setData((prev) => ({
      domainGroups: flatten(allData?.map((el) => el.domainGroups) || []),
      total: prev.total,
      totalDomain: prev.totalDomain,
    }));
    setRefetching(false);
  }, [filters]);

  //* Refetch with changing loading state
  const refetchWithLoading = useCallback(
    async (shouldSetData) => {
      try {
        setLoading(true);
        const response = await fetch();
        if (shouldSetData) {
          checkConditionPass(response);
        }
        setLoading(false);
      } catch (error) {
        showError(error);
        setLoading(false);
      }
    },
    [fetch, checkConditionPass]
  );

  useEffect(() => {
    let shouldSetData = true;
    if (isDefine(filters.offset) && filters.offset <= 0) {
      refetchWithLoading(shouldSetData);
      return;
    }

    if (isDefine(filters.page) && filters.page <= 0) {
      refetchWithLoading(shouldSetData);
      return;
    }

    //* If offset > 0 -> fetch more
    fetchMore(shouldSetData);

    return () => {
      shouldSetData = false;
    };
  }, [filters.page, filters.offset, fetchMore, refetchWithLoading]);

  return {
    data,
    loading,
    error,
    refetch,
    refetchWithLoading,
    refetching,
    loadingMore,
    hasMore,
    setData,
  };
};

export default useGetAllDomainGroup;
